import React, { useEffect, useRef, useState } from "react";
import ZoomInRoundedIcon from '@mui/icons-material/ZoomInRounded';
import { queryData } from "../../utils/api";

export default function Sparkline({ 
  variables, 
  locationName, 
  deviceId, 
  deviceType,
  width = 95, 
  height = 47, 
  onClick, 
  title,
  seriesColors 
}) {
  const ref = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [data, setData] = useState([]);
  
  useEffect(() => {
    // Fetch sparkline data from API - always use UTC
    if (!variables || !locationName || !deviceId) return;
    
    const promises = variables.map(variable => {
      return queryData({
        start: "-24h",
        timezone: "UTC",
        filter: {
          location: locationName,
          device_id: deviceId,
          name: variable
        }
      }).then(records => ({
        variable,
        records
      }));
    });
    
    Promise.all(promises)
      .then(results => {
        setData(results);
      })
      .catch(err => {
        console.error("Failed to fetch sparkline data:", err);
      });
  }, [variables, locationName, deviceId]);
  
  useEffect(() => {
    const c = ref.current;
    if (!c || !data.length) return;
    
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, width, height);
    
    const pad = 2;
    const W = width;
    const H = height;
    
    // Process each variable's data
    data.forEach(({ variable, records }, seriesIndex) => {
      if (!records.length) return;
      
      const pts = records.map(r => [
        new Date(r.timestamp).getTime(),
        r.value
      ]).sort((a, b) => a[0] - b[0]);
      
      const xs = pts.map(p => p[0]);
      const ys = pts.map(p => p[1]);
      
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);
      
      const xOf = (t) => pad + ((t - minX) / (maxX - minX || 1)) * (W - 2 * pad);
      const yOf = (v) => pad + (1 - (v - minY) / (maxY - minY || 1)) * (H - 2 * pad);
      
      // Set color
      const color = seriesColors && seriesColors[seriesIndex] 
        ? seriesColors[seriesIndex] 
        : "#0e7eb7";
      
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.beginPath();
      
      pts.forEach(([t, v], i) => {
        const x = xOf(t);
        const y = yOf(v);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      
      ctx.stroke();
    });
    
    // Draw baseline
    ctx.strokeStyle = "#e5e7eb";
    ctx.beginPath();
    ctx.moveTo(pad, H - pad - 0.5);
    ctx.lineTo(W - pad, H - pad - 0.5);
    ctx.stroke();
    
    // Apply dim overlay if hovered
    if (isHovered) {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
      ctx.fillRect(0, 0, width, height);
    }
  }, [data, width, height, isHovered, seriesColors]);
  
  return (
    <div 
      style={{ 
        position: 'relative', 
        display: 'inline-block',
        cursor: 'pointer'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
      title={title || "Open in Data"}
    >
      <canvas
        ref={ref}
        width={width}
        height={height}
        style={{ display: "block", width: `${width}px`, height: `${height}px` }}
      />
      {isHovered && (
        <ZoomInRoundedIcon 
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: '#f2f2f2',
            pointerEvents: 'none',
            fontSize: '2rem'
          }}
        />
      )}
    </div>
  );
}